import type { PaymentDraft, PaymentMethod, Recipient, Transaction } from '../../types';
import { MOCK_PAYMENT_METHODS, MOCK_RECIPIENTS } from '../../lib/mockData';
import { makeId, request } from '../api/client';

export interface ProcessResult {
  transaction: Transaction;
}

export const paymentService = {
  listRecipients(query?: string) {
    return request('/recipients', () => {
      if (!query) return MOCK_RECIPIENTS;
      const q = query.toLowerCase();
      return MOCK_RECIPIENTS.filter(
        (r) => r.name.toLowerCase().includes(q) || r.handle.toLowerCase().includes(q),
      );
    });
  },

  listMethods() {
    return request('/payment-methods', () => MOCK_PAYMENT_METHODS);
  },

  calculateFee(amount: number, method: PaymentMethod) {
    return request('/fees', () => {
      const processing = Math.round(amount * (method.feePct / 100) * 100) / 100;
      const network = method.type === 'crypto' ? 1.25 : 0;
      const platform = Math.round(amount * 0.001 * 100) / 100;
      return { processing, network, platform, total: processing + network + platform };
    }, { delay: 200 });
  },

  /** Submit a payment for processing. Occasionally fails to surface error UX. */
  process(draft: PaymentDraft) {
    return request('/payments', () => buildResult(draft), { delay: 2200, failRate: 0.12 });
  },
};

function buildResult(draft: PaymentDraft) {
  const tx: Transaction = {
    id: makeId('tx'),
    reference: `PSK-${Math.random().toString(36).slice(2, 8).toUpperCase()}`,
    direction: 'sent',
    category: draft.category,
    amount: draft.amount ?? 0,
    currency: draft.currency,
    fee: Math.round((draft.amount ?? 0) * 0.012 * 100) / 100,
    counterparty: draft.recipient?.name ?? 'Unknown',
    counterpartyHandle: draft.recipient?.handle,
    method: draft.method?.type ?? 'wallet',
    status: 'completed',
    note: draft.note,
    date: new Date().toISOString(),
  };
  return { transaction: tx };
}

export function emptyDraft(): PaymentDraft {
  return { currency: 'USD', category: 'transfer' };
}

export type { Recipient, Transaction };
